import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, Platform, ActivityIndicator, TextInput, ScrollView } from 'react-native';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import { ModuleHeader, LodgingCard, FullscreenPoiModal, PlatformIcon } from '@cubemeet/ui';
import { MdFlight, MdStyle, MdVisibility, MdFavorite, MdInsertComment, MdLocationOn, MdSearch, MdTune, MdClose } from 'react-icons/md';
import { CITY_DATA, CITY_MATCH_TOKENS } from '../data/cities';
import { CITY_MAP_CONFIG } from '../data/cityMapConfig';
import NearbyMap, { NearbySpot } from '../components/NearbyMap';
import { useOverpassPois } from '@cubemeet/ui/hooks/useOverpassPois';
import { createPayloadFromOSM } from '../utils/osmToPayload';
import { supabase } from '@cubemeet/core/services/supabase';
import { useGlobalToast } from '../contexts/ToastContext';

type CityMeta = {
  key: string;
  name: string;
  cover: string;
  intro: string;
  seoTitle?: string;
  seoDescription?: string;
};

// Map configuration moved to shared data/cityMapConfig.ts for reuse across all city pages.

const OSM_CATEGORY_ICONS: Record<string, string> = {
  poi: '/assets/icons/poi.png',
  restaurant: '/assets/icons/restaurant.png',
  shopping: '/assets/icons/shopping.png',
  lodging: '/assets/icons/lodging.png',
};

const RESTAURANT_TYPES = new Set([
  'restaurant',
  'cafe',
  'fast_food',
  'food_court',
  'bar',
  'pub',
  'biergarten',
]);

const LODGING_TYPES = new Set([
  'hotel',
  'guest_house',
  'hostel',
  'motel',
  'apartment',
  'chalet',
  'resort',
  'love_hotel',
  'camp_site',
  'caravan_site',
]);

const SHOPPING_TYPES = new Set([
  'mall',
  'department_store',
  'supermarket',
  'convenience',
  'clothes',
  'shoes',
  'gift',
  'souvenir',
  'electronics',
  'cosmetics',
  'beauty',
  'bakery',
  'butcher',
  'greengrocer',
  'marketplace',
  'wholesale',
  'toy',
  'books',
  'stationery',
  'kiosk',
  'florist',
  'jewelry',
]);

const POI_TYPES = new Set([
  'attraction',
  'viewpoint',
  'museum',
  'gallery',
  'art_gallery',
  'theme_park',
  'zoo',
  'aquarium',
  'park',
  'playground',
  'theatre',
  'cinema',
  'memorial',
  'heritage',
  'historic',
  'temple',
  'shrine',
  'church',
  'cathedral',
  'castle',
  'monument',
  'tourist_attraction',
]);

const normalizeOsmCategory = (rawType?: string): 'poi' | 'restaurant' | 'shopping' | 'lodging' | null => {
  if (!rawType) return null;
  const type = rawType.toLowerCase();
  if (RESTAURANT_TYPES.has(type)) return 'restaurant';
  if (LODGING_TYPES.has(type)) return 'lodging';
  if (SHOPPING_TYPES.has(type)) return 'shopping';
  if (POI_TYPES.has(type)) return 'poi';
  return null;
};

const pickOsmIcon = (category: 'poi' | 'restaurant' | 'shopping' | 'lodging'): string => {
  return OSM_CATEGORY_ICONS[category] || OSM_CATEGORY_ICONS.poi;
};

const MAX_OSM_PER_CATEGORY = 12;
const MAX_OSM_TOTAL = 48;

const toRadians = (deg: number) => (deg * Math.PI) / 180;
const haversineDistanceMeters = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371000; // meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const parseCoordinate = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const num = Number.parseFloat(value);
    if (Number.isFinite(num)) return num;
  }
  return null;
};


const CityPage: React.FC = () => {
  const { cityKey } = useParams();
  const navigate = useNavigate();
  const reduxUser = useSelector((s: any) => s?.auth?.user);
  const currentUserId = reduxUser?.id;
  const { showSuccess, showError } = useGlobalToast();

  // Detect whether we should show a back/referral arrow.
  const location = useLocation();
  const _search = typeof location?.search === 'string' ? location.search : (typeof window !== 'undefined' ? window.location.search : '');
  const _params = new URLSearchParams(_search);
  // Accept multiple common variants for referral/referrer query keys
  const refererParam = _params.get('referer') || _params.get('ref') || _params.get('referal') || _params.get('referrer') || _params.get('referral');
  // Consider: explicit query param, document.referrer, router state (location.state.from), or browser history length
  const hasReferer = Boolean(refererParam)
    || (typeof document !== 'undefined' && !!document.referrer)
    || Boolean((location as any)?.state?.from)
    || (typeof window !== 'undefined' && !!(window.history && window.history.length && window.history.length > 1));

  const handleBack = React.useCallback(() => {
    try {
      const candidate = (refererParam || (typeof document !== 'undefined' ? document.referrer : '') || '').toString();

      if (candidate) {
        // If candidate looks like a relative path, navigate directly
        if (candidate.startsWith('/')) {
          try { navigate(candidate); return; } catch (e) { /* continue to other fallbacks */ }
        }

        // Try to resolve as URL; if same-origin use router navigate, otherwise full redirect
        try {
          const resolved = new URL(candidate, window.location.href);
          if (resolved.origin === window.location.origin) {
            try { navigate(resolved.pathname + resolved.search + resolved.hash); return; } catch (e) { /* continue */ }
          }
          // external origin: perform full page redirect
          window.location.href = resolved.href;
          return;
        } catch (e) {
          // not a valid absolute URL — try to use history.back as a last resort for weird values
          if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
            try { window.history.back(); return; } catch (err) { /* continue */ }
          }
        }
      }

      // Fallbacks: use native history.back if available, else router back, else go home
      if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
        try { window.history.back(); return; } catch (e) { /* continue */ }
      }

      try { navigate(-1); return; } catch (e) { /* continue */ }
      navigate('/');
    } catch (err) {
      try { navigate('/'); } catch (e) { /* swallow */ }
    }
  }, [navigate, refererParam]);

  const city = useMemo(() => {
    if (!cityKey) return null;
    const key = String(cityKey).toLowerCase();
    return CITY_DATA[key] ?? null;
  }, [cityKey]);

  // State for POI modal
  const [showPoiModal, setShowPoiModal] = useState(false);
  const [poiPayload, setPoiPayload] = useState<any>(null);

  // 控制是否載入 / 顯示 OSM 資料（預設關閉，只先顯示自家卡片）
  // NOTE: default false so the toggle is OFF on first render; user can enable it.
  const [showOsm, setShowOsm] = useState<boolean>(false);

  // Tabs & Filter State
  const [activeTab, setActiveTab] = useState<'posts' | 'cards'>('posts');
  const [cityPosts, setCityPosts] = useState<any[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  
  const [cardSearchQuery, setCardSearchQuery] = useState('');
  const [selectedCardTypes, setSelectedCardTypes] = useState<string[]>([]);
  const [filteredCards, setFilteredCards] = useState<any[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'popular'>('newest');

  const cardTypes = [
    { key: 'poi', label: '景点', icon: 'place' },
    { key: 'restaurant', label: '餐厅', icon: 'restaurant' },
    { key: 'lodging', label: '住宿', icon: 'hotel' },
    { key: 'activity', label: '活动', icon: 'event' },
    { key: 'shopping', label: '购物', icon: 'shopping_cart' },
  ];

  // stable city name for closures inside useEffect (avoid accidental ReferenceError in some runtimes)
  const cityName = city ? city.name : '';
  const mapConfig = city ? CITY_MAP_CONFIG[city.key] : undefined;

  const { data: osmPoisData, loading: osmPoisLoading } = useOverpassPois({
    // 只有在使用者開啟 OSM 開關時才去抓取 OSM 資料
    enabled: Boolean(mapConfig) && showOsm,
    allowPublicEndpoint: true,
    center: mapConfig
      ? { lat: mapConfig.center[0], lon: mapConfig.center[1], radius: mapConfig.radius }
      : undefined,
    types: [],
  });


  // real card data fetched from cards_cards
  const [cardsByType, setCardsByType] = useState<Record<string, any[]>>({});
  // Simple banner for a city guide post
  const GuideBanner: React.FC<{ post: any }> = ({ post }) => {
    const nav = useNavigate();
    return (
      <TouchableOpacity
        activeOpacity={0.9}
        onPress={() => nav(`/travel-post/${post.id}`)}
        style={{
          borderRadius: 12,
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: '#E5E7EB',
          marginBottom: 16,
          backgroundColor: '#fff',
        }}
      >
        {post.cover_image ? (
          <Image source={{ uri: post.cover_image }} style={{ width: '100%', aspectRatio: 16/7 } as any} />
        ) : null}
        <View style={{ padding: 12 }}>
          <Text style={{ fontSize: 12, color: '#10B981', marginBottom: 4 }}>城市攻略</Text>
          <Text style={{ fontSize: 16, fontWeight: '700', color: '#111827' }} numberOfLines={2}>{post.title || '城市攻略'}</Text>
          {post.summary ? (
            <Text style={{ fontSize: 13, color: '#6B7280', marginTop: 6 }} numberOfLines={3}>{post.summary}</Text>
          ) : null}
        </View>
      </TouchableOpacity>
    );
  };
  const [fetchAttempted, setFetchAttempted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guidePost, setGuidePost] = useState<any | null>(null);

  // 中間按鈕：盡量讓 AppLayout 打開半屏模態；如果沒有 AppLayout 再回退到路由
  const handleCenterPress = () => {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      try {
        window.dispatchEvent(new CustomEvent('openCreateSheet'));
        return;
      } catch (e) {
        // 忽略錯誤並回退到直接跳轉
      }
    }
    navigate('/travel/create');
  };

  const cardSpots = useMemo<NearbySpot[]>(() => {
    const results: NearbySpot[] = [];
    const seen = new Set<string>();
    const candidateTypes = ['poi', 'restaurant', 'lodging', 'shopping'] as const;
    candidateTypes.forEach((type) => {
      const list = cardsByType[type] || [];
      list.forEach((card) => {
        const lat = parseCoordinate((card as any)?.latitude ?? (card as any)?.lat ?? (card as any)?.location?.latitude ?? (card as any)?.location?.lat ?? (card as any)?.base_payload?.latitude ?? (card as any)?.base_payload?.lat);
        const lon = parseCoordinate((card as any)?.longitude ?? (card as any)?.lng ?? (card as any)?.location?.longitude ?? (card as any)?.location?.lng ?? (card as any)?.base_payload?.longitude ?? (card as any)?.base_payload?.lng);
        if (lat == null || lon == null) return;
        const key = `${(card as any)?.id || 'card'}:${lat.toFixed(6)}:${lon.toFixed(6)}`;
        if (seen.has(key)) return;
        seen.add(key);
        results.push({
          id: `card:${(card as any)?.id ?? `${lat}:${lon}`}`,
          name: (card as any)?.title || '未命名卡片',
          lat,
          lng: lon,
          imageUrl: (card as any)?.cover_image || undefined,
          description: (card as any)?.description || undefined,
          address: formatAddress(card) || undefined,
          category: type,
          source: 'card' as const,
        });
      });
    });
    return results;
  }, [cardsByType]);

  const osmSpots = useMemo<NearbySpot[]>(() => {
    // 未開啟 OSM 開關時完全不輸出 OSM spots，只顯示自家卡片
    if (!showOsm || !mapConfig || !osmPoisData || !Array.isArray(osmPoisData)) return [];
    type CategorizedSpot = { spot: NearbySpot; category: 'poi' | 'restaurant' | 'shopping' | 'lodging' };
    const candidates: CategorizedSpot[] = [];
    (osmPoisData as any[]).forEach((poi) => {
      const lat = parseCoordinate(poi?.latitude ?? poi?.lat);
      const lon = parseCoordinate(poi?.longitude ?? poi?.lon);
      if (lat == null || lon == null) return;
      const category = normalizeOsmCategory(poi?.card_type);
      if (!category) return;
      const icon = pickOsmIcon(category);
      candidates.push({
        category,
        spot: {
          id: poi?.id || `osm:${lat.toFixed(6)}:${lon.toFixed(6)}`,
          name: poi?.title || 'OSM POI',
          lat,
          lng: lon,
          imageUrl: icon,
          description: poi?.description || undefined,
          address: poi?.address || undefined,
          category,
          source: 'osm' as const,
          website: poi?.website || (poi?.raw && poi?.raw.tags && (poi.raw.tags.website || poi.raw.tags['contact:website'] || poi.raw.tags.url)) || undefined,
          phone: poi?.phone || (poi?.raw && poi?.raw.tags && (poi.raw.tags.phone || poi.raw.tags['contact:phone'] || poi.raw.tags['contact:tel'] || poi.raw.tags.tel)) || undefined,
          opening_hours: poi?.opening_hours || (poi?.raw && poi?.raw.tags && poi.raw.tags.opening_hours) || undefined,
          tags: poi?.raw && poi?.raw.tags ? poi.raw.tags : undefined,
        },
      });
    });
    if (!candidates.length) return [];
    const [centerLat, centerLon] = mapConfig.center;
    const sorted = candidates.sort((a, b) => {
      const distA = haversineDistanceMeters(centerLat, centerLon, a.spot.lat, a.spot.lng);
      const distB = haversineDistanceMeters(centerLat, centerLon, b.spot.lat, b.spot.lng);
      return distA - distB;
    });
    const limited: NearbySpot[] = [];
    const perCategory = new Map<string, number>();
    sorted.forEach(({ category, spot }) => {
      if (limited.length >= MAX_OSM_TOTAL) return;
      const current = perCategory.get(category) ?? 0;
      if (current >= MAX_OSM_PER_CATEGORY) return;
      perCategory.set(category, current + 1);
      limited.push(spot);
    });
    return limited;
  }, [mapConfig, osmPoisData, showOsm]);

  const nearbySpots = useMemo<NearbySpot[]>(() => {
    // 地圖永遠會先顯示自家卡片；只有在 showOsm=true 時才合併 OSM spots
    if (!cardSpots.length && !osmSpots.length) return [];
    if (!showOsm) return cardSpots;
    return [...cardSpots, ...osmSpots];
  }, [cardSpots, osmSpots, showOsm]);

  const handleSpotSelect = useCallback((spotId: string) => {
    if (!spotId) return;
    if (spotId.startsWith('card:')) {
      const targetId = spotId.slice(5);
      if (targetId) navigate(`/card/${targetId}`);
      return;
    }
    // OSM POI clicks are now handled by onCreateCard
  }, [navigate]);

  const handleCreateCard = useCallback(async (spot: NearbySpot) => {
    console.log('[CityPage] Creating card from OSM POI:', spot);
    console.log('[CityPage] Spot has tags?', {
      hasTags: !!spot.tags,
      tagsType: typeof spot.tags,
      tagsKeys: spot.tags ? Object.keys(spot.tags) : [],
      phone: spot.phone,
      website: spot.website,
      opening_hours: spot.opening_hours,
    });

    // Try to enrich address via Nominatim when missing
    let enrichedAddress: string | undefined = spot.address;
    // Structured location fields (city/country/province/district)
    let structuredLoc: { city?: string; country?: string; province?: string; district?: string } = {};
    try {
      if (typeof fetch !== 'undefined') {
        const resp = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(spot.lat)}&lon=${encodeURIComponent(spot.lng)}&addressdetails=1&accept-language=zh-CN,zh-TW,ja,en`,
          { headers: { 'Accept': 'application/json', 'User-Agent': 'CubeMeet/1.0 (+https://cubemeet.app)' } }
        );
        if (resp.ok) {
          const j = await resp.json();
          enrichedAddress = (j && (j.display_name || (j.address && Object.values(j.address).filter(Boolean).join(', ')))) || enrichedAddress;
          try {
            const a = j?.address || {};
            // Map best-effort to desired keys
            const city = a.city || a.town || a.village || a.city_district || a.municipality;
            const district = a.district || a.suburb || a.neighbourhood || a.city_district || a.county; // Nominatim keys vary by country
            const province = a.province || a.state || a.region || a.state_district;
            const country = a.country || a.country_code;
            structuredLoc = {
              ...(city ? { city } : {}),
              ...(country ? { country } : {}),
              ...(district ? { district } : {}),
              ...(province ? { province } : {}),
            };
          } catch {}
        }
      }
    } catch (e) {
      // non-fatal
    }

    // Convert OSM data to card payload (already extracts addr:* tags)
    const payload = createPayloadFromOSM({ ...spot, address: enrichedAddress || spot.address });

    // Add city information to the payload (for page context)
    if (city) {
      // Only add city name if location doesn't already have it from tags
      if (!payload.location || !payload.location.city) {
        payload.location = {
          ...(payload.location || {}),
          city: city.name,
        };
      }
      payload.tags = [...(payload.tags || []), city.key];

      // If still no address, fallback to city name
      if (!payload.address || payload.address === '') {
        payload.address = city.name;
      }
    }

    // Merge structured location from reverse geocode (only fill missing fields)
    // IMPORTANT: Prefer page context city name over reverse geocoding to ensure consistent matching
    if (structuredLoc && Object.keys(structuredLoc).length) {
      const existingLoc = payload.location || {};
      payload.location = {
        // Use page city name (e.g., "大阪") instead of reverse geocoding result (e.g., "大阪市", "Osaka")
        city: (city && city.name) || existingLoc.city || structuredLoc.city,
        country: existingLoc.country || structuredLoc.country,
        district: existingLoc.district || structuredLoc.district,
        province: existingLoc.province || structuredLoc.province,
      };
      // Remove undefined values
      Object.keys(payload.location).forEach(k => {
        if (payload.location[k] === undefined) delete payload.location[k];
      });
    }

    console.log('[CityPage] Generated payload:', payload);

    // Open the FullscreenPoiModal with prefilled data
    setPoiPayload(payload);
    setShowPoiModal(true);
  }, [city]);

  // Force unlock scroll when component unmounts (e.g., navigating away)
  useEffect(() => {
    return () => {
      // Cleanup: force unlock scroll when leaving page
      if (typeof document !== 'undefined') {
        try {
          const body = document.body;
          const html = document.documentElement;
          body.dataset.scrollLockCount = '0';
          html.style.overflow = 'visible';
          body.style.overflow = 'visible';
          delete html.dataset.scrollLockPrev;
          delete body.dataset.scrollLockPrev;
        } catch (e) {
          console.error('Failed to cleanup scroll lock:', e);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!city) return;
    let mounted = true;

    const fetchForType = async (type: string, client: any) => {
      try {
        const cityName = city.name;
        const cityKey = city.key;
          const tokens = (CITY_MATCH_TOKENS && CITY_MATCH_TOKENS[cityKey]) ? CITY_MATCH_TOKENS[cityKey] : [cityName];

        // 1) Prefer jsonb containment on location { city: token } for any token variant
        try {
          for (const tkn of tokens) {
            try {
              const { data, error } = await client
                .from('cards_cards')
                .select('id,title,description,cover_image,address,card_type,base_payload,latitude,longitude,price_range')
                .eq('status', 'active')
                .eq('card_type', type)
                .contains('location', { city: tkn })
                .limit(8);
              if (!error && data && data.length) {
                console.debug(`CityPage: fetched ${data.length} items for ${type} via location.contains(city=${tkn})`);
                return data;
              }
            } catch (innerErr) {
              // try next token
              console.debug(`CityPage: contains() with token ${tkn} failed, trying next`, innerErr);
            }
          }
        } catch (e) {
          // ignore and continue to next strategy
          console.debug('CityPage: contains() outer query failed, will try other strategies', e);
        }

        // 2) Try location.city with ilike for fuzzy matching (e.g., "大阪" matches "大阪市", "Osaka")
        try {
          for (const tkn of tokens) {
            try {
              const { data: idata, error: ierr } = await client
                .from('cards_cards')
                .select('id,title,description,cover_image,address,card_type,base_payload,latitude,longitude,price_range')
                .eq('status', 'active')
                .eq('card_type', type)
                .ilike('location->>city', `%${tkn}%`)
                .limit(8);
              if (!ierr && idata && idata.length) {
                console.debug(`CityPage: fetched ${idata.length} items for ${type} via location.city.ilike(%${tkn}%)`);
                return idata;
              }
            } catch (innerErr) {
              console.debug(`CityPage: ilike with token ${tkn} failed, trying next`, innerErr);
            }
          }
        } catch (e) {
          console.debug('CityPage: ilike query failed', e);
        }

        // 3) Try tags contains (some rows store cityKey in tags)
        try {
          if (cityKey) {
            const { data: tdata, error: terr } = await client
              .from('cards_cards')
              .select('id,title,description,cover_image,address,card_type,base_payload,latitude,longitude,price_range')
              .eq('status', 'active')
              .eq('card_type', type)
              .contains('tags', [cityKey])
              .limit(8);
            if (!terr && tdata && tdata.length) {
              console.debug(`CityPage: fetched ${tdata.length} items for ${type} via tags.contains(${cityKey})`);
              return tdata;
            }
          }
        } catch (e) {
          console.debug('CityPage: tags.contains() query failed', e);
        }

        // 4) Fallback to ilike on formatted_address or city inside location JSON for any token
        try {
          // build or expression that checks both formatted_address and city for each token
          const clauses: string[] = [];
          for (const tkn of tokens) {
            const esc = tkn.replace(/%/g, '\\%').replace(/,/g, '');
            clauses.push(`location->>formatted_address.ilike.%${esc}%`);
            clauses.push(`location->>city.ilike.%${esc}%`);
          }
          const orExpr = clauses.join(',');
          const { data: odata, error: oerr } = await client
            .from('cards_cards')
            .select('id,title,description,cover_image,address,card_type,base_payload,latitude,longitude,price_range')
            .eq('status', 'active')
            .eq('card_type', type)
            .or(orExpr)
            .limit(8);
          if (!oerr && odata && odata.length) {
            console.debug(`CityPage: fetched ${odata.length} items for ${type} via or(ilike) tokens=${tokens.join('|')}`);
            return odata;
          }
        } catch (e) {
          console.debug('CityPage: or(ilike) query failed', e);
        }

        // if none matched, return empty
        return [];
      } catch (e) {
        console.warn('CityPage: fetch exception (outer)', e);
        return [];
      }
    };

    const fetchAll = async () => {
      setLoading(true);
      // load supabase client dynamically to avoid bundler import-analysis issues
      try {
        const mod = await import('../../../../packages/core/src/services/supabase');
        const client = (mod && (mod as any).supabase) ? (mod as any).supabase : null;
        if (!client) {
          console.warn('CityPage: supabase client not available');
          setFetchAttempted(true);
          setLoading(false);
          return;
        }
        // Helper: fetch top city guide post
        const fetchCityGuide = async () => {
          try {
            // Map cityKey -> expected content_json city string
            const cityKeyLower = (city.key || '').toLowerCase();
              const cityMap: Record<string, string> = {
              tokyo: 'Tokyo,东京,東京',
              osaka: 'osaka,大阪,大阪',
              kyoto: 'Kyoto,京都,京都',
              hongkong: 'Hong Kong,香港,香 港',
              shanghai: 'Shanghai,上海,上海市',
              beijing: 'Beijing,北京,北京市',
              singapore: 'Singapore,新加坡,新加坡',
              'kuala-lumpur': 'Kuala Lumpur,吉隆坡,吉隆坡',
              macau: 'Macau,澳門,澳门',
              bangkok: 'Bangkok,曼谷,曼谷',
              taipei: 'Taipei,台北,臺北',
              fuji: 'Fuji,富士山,富士',
            };
            const expectedCityString = cityMap[cityKeyLower];
            // First try: exact match on JSON fields (root object)
            if (expectedCityString) {
              const { data, error } = await client
                .from('travel_posts')
                .select('id,title,summary,cover_image,content_json,created_at,view_count,like_count,comment_count')
                .eq('status', 'published')
                .eq('content_json->>type', 'guide')
                .eq('content_json->>city', expectedCityString)
                .order('created_at', { ascending: false })
                .limit(1);
              if (!error && data && data.length) return data[0];

              // Second: containment on object (jsonb @>) – Supabase contains can work on JSON objects
              const { data: d3 } = await client
                .from('travel_posts')
                .select('id,title,summary,cover_image,content_json,created_at,view_count,like_count,comment_count')
                .eq('status', 'published')
                .contains('content_json', { city: expectedCityString, type: 'guide' })
                .order('created_at', { ascending: false })
                .limit(1);
              if (d3 && d3.length) return d3[0];

              // Third: try partial tokens from the alias string
              const tokens = expectedCityString.split(',').map((s) => s.trim()).filter(Boolean);
              for (const token of tokens) {
                const { data: d4 } = await client
                  .from('travel_posts')
                  .select('id,title,summary,cover_image,content_json,created_at,view_count,like_count,comment_count')
                  .eq('status', 'published')
                  .eq('content_json->>type', 'guide')
                  .ilike('content_json->>city', `%${token}%`)
                  .order('created_at', { ascending: false })
                  .limit(1);
                if (d4 && d4.length) return d4[0];
              }
            }

            // Fallback: any guide where content_json textual includes city name
            const { data: d2 } = await client
              .from('travel_posts')
              .select('id,title,summary,cover_image,content_json,created_at,view_count,like_count,comment_count')
              .eq('status', 'published')
              .eq('content_json->>type', 'guide')
              .filter('content_json::text', 'ilike', `%${city.name}%`)
              .order('created_at', { ascending: false })
              .limit(1);
            if (d2 && d2.length) return d2[0];

            return null;
          } catch (err) {
            console.warn('CityPage: fetchCityGuide failed', err);
            return null;
          }
        };

        const types = ['poi', 'restaurant', 'activity', 'shopping', 'lodging'];
        // fetch in parallel to reduce wait time (cards + guide)
        const fetched = await Promise.all([
          ...types.map((t) => fetchForType(t, client)),
          fetchCityGuide(),
        ]);
        if (!mounted) return;
        const result: Record<string, any[]> = {};
        types.forEach((t, idx) => { result[t] = fetched[idx] as any[] || []; });
        const guide = fetched[types.length];
        if (mounted) setCardsByType(result);
        if (mounted) setGuidePost(guide || null);
        setFetchAttempted(true);
      } catch (e) {
        console.warn('CityPage: failed to dynamically import supabase client', e);
        setFetchAttempted(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();

    return () => { mounted = false; };
  }, [city]);

  // Fetch Posts for Tab
  useEffect(() => {
    if (activeTab === 'posts' && city) {
      const fetchPosts = async () => {
        setPostsLoading(true);
        try {
          const mod = await import('../../../../packages/core/src/services/supabase');
          const client = (mod && (mod as any).supabase) ? (mod as any).supabase : null;
          if (!client) return;

          const cityKey = city.key;
          // Try to match city name or key
          const { data, error } = await client
            .from('travel_posts')
            .select('id,title,summary,cover_image,content_json,created_at,view_count,like_count,comment_count')
            .eq('status', 'published')
            .or(`content_json->>city.ilike.%${city.name}%,content_json->>city.ilike.%${cityKey}%`)
            .order('created_at', { ascending: false })
            .limit(20);

          if (!error && data) {
             setCityPosts(data.map(p => ({
               ...p,
               city: p.content_json?.city || city.name
             })));
          }
        } catch (e) {
          console.error(e);
        } finally {
          setPostsLoading(false);
        }
      };
      fetchPosts();
    }
  }, [activeTab, city]);

  // Fetch Cards for Tab
  useEffect(() => {
    if (activeTab === 'cards' && city) {
      const fetchCards = async () => {
        setCardsLoading(true);
        try {
          const mod = await import('../../../../packages/core/src/services/supabase');
          const client = (mod && (mod as any).supabase) ? (mod as any).supabase : null;
          if (!client) return;

          let query = client
            .from('cards_cards')
            .select('id,title,description,cover_image,address,card_type,base_payload,latitude,longitude,price_range,tags,location,view_count')
            .eq('status', 'active');

          if (selectedCardTypes.length > 0) {
            query = query.in('card_type', selectedCardTypes);
          }
          
          if (cardSearchQuery) {
            query = query.ilike('title', `%${cardSearchQuery}%`);
          }

          // City matching - Robust OR logic
          const cityKey = city.key;
          const tokens = (CITY_MATCH_TOKENS && CITY_MATCH_TOKENS[cityKey]) ? CITY_MATCH_TOKENS[cityKey] : [city.name];
          
          const orConditions: string[] = [];
          tokens.forEach(t => {
             const safeT = t.replace(/,/g, '');
             orConditions.push(`location->>city.ilike.%${safeT}%`);
             orConditions.push(`location->>formatted_address.ilike.%${safeT}%`);
          });
          
          if (cityKey) {
             orConditions.push(`tags.cs.{${cityKey}}`);
          }
          
          if (orConditions.length > 0) {
             query = query.or(orConditions.join(','));
          }

          // Sorting
          if (sortOrder === 'popular') {
            query = query.order('view_count', { ascending: false });
          } else {
            query = query.order('created_at', { ascending: false });
          }

          const { data, error } = await query.limit(50);
          
          if (!error && data) {
            setFilteredCards(data);
          }
        } catch (e) {
          console.error(e);
        } finally {
          setCardsLoading(false);
        }
      };
      
      const timeout = setTimeout(fetchCards, 300);
      return () => clearTimeout(timeout);
    }
  }, [activeTab, city, cardSearchQuery, selectedCardTypes, sortOrder]);

  useEffect(() => {
    const title = city?.seoTitle || (city ? `${city.name} · CubeMeet` : '城市');
    const desc = city?.seoDescription || city?.intro || '';
    if (typeof document !== 'undefined') {
      document.title = title;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', desc);
    }
  }, [city]);

  const displayPosts = useMemo(() => {
    if (!guidePost) return cityPosts;
    const filtered = cityPosts.filter(p => p.id !== guidePost.id);
    return [guidePost, ...filtered];
  }, [guidePost, cityPosts]);

  if (!city) {
    return (
      <View style={styles.container}>
        <ModuleHeader moduleType="travel" title="城市" subtitle="城市占位页" showMiniCube topInset={0} showBackButton onBack={() => navigate(-1)} />
        <View style={styles.content}><Text>未找到该城市。</Text></View>
      </View>
    );
  }

  const handleShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const shareText = `${city.name} - ${city.intro}`;
    try {
      if (navigator && (navigator as any).share) {
        await (navigator as any).share({ title: city.name, text: shareText, url });
        return;
      }
    } catch (e) {
      // ignore and fallback to copy
    }
    if (navigator && (navigator as any).clipboard) {
      try {
        await (navigator as any).clipboard.writeText(url);
        window.alert('已复制页面链接');
        return;
      } catch (e) {
        // ignore
      }
    }
    // last resort
    window.prompt('复制此链接以分享', url);
  };

  return (
    <>
    <View style={styles.container}>
      <View style={styles.heroOuterContainer}>
        <View style={styles.heroInnerContainer}>
          <View style={[styles.heroWrap as any]}>
            <Image source={{ uri: city.cover }} style={styles.heroImage as any} />
            {hasReferer && (
              <TouchableOpacity
                activeOpacity={0.9}
                onPress={handleBack}
                style={styles.backButton as any}
              >
                <PlatformIcon name="arrow_back" size={22} color="#fff" family="MaterialIcons" />
              </TouchableOpacity>
            )}
            <View style={styles.heroOverlay} />
            <View style={styles.heroText}>
              <Text style={styles.heroTitle}>{city.name}</Text>
              <Text style={styles.heroIntro} numberOfLines={3}>{city.intro}</Text>
            </View>
            <TouchableOpacity style={styles.shareButton} onPress={handleShare} activeOpacity={0.8} accessibilityLabel="分享">
              <PlatformIcon name="share" size={22} color="#fff" family="MaterialIcons" />
            </TouchableOpacity>
          </View>
        </View>
      </View>

      <View style={[styles.content, { paddingBottom: 100 }]}>
        <View style={styles.inner}>
          {(loading || !fetchAttempted) && (
          <View style={{ alignItems: 'center', paddingVertical: 24 }}>
            <ActivityIndicator size="small" color="#3B82F6" />
            <Text style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>正在加载城市数据…</Text>
          </View>
        )}

        {!loading && fetchAttempted && (
          <>
            {mapConfig ? (
              <View style={{ marginBottom: 20 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={styles.sectionTitle}>{`${city.name} 周边地图`}</Text>
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={() => setShowOsm(prev => !prev)}
                    style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 4 }}
                  >
                    <View
                      style={{
                        width: 34,
                        height: 20,
                        borderRadius: 10,
                        backgroundColor: showOsm ? '#22C55E' : '#E5E7EB',
                        marginRight: 6,
                        justifyContent: 'center',
                        paddingHorizontal: 3,
                      }}
                    >
                      <View
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 7,
                          backgroundColor: '#fff',
                          transform: [{ translateX: showOsm ? 12 : 0 }],
                        }}
                      />
                    </View>
                    <Text style={{ fontSize: 12, color: '#4B5563' }}>{showOsm ? '显示 OSM 景点' : '只看卡片'}</Text>
                  </TouchableOpacity>
                </View>
                <NearbyMap
                  center={mapConfig.center}
                  spots={nearbySpots}
                  primaryLabel={mapConfig.primaryLabel}
                  primaryAddress={mapConfig.primaryAddress}
                  primaryImageUrl={city.cover}
                  primaryName={city.name}
                  onSelectSpot={handleSpotSelect}
                  onCreateCard={handleCreateCard}
                  initialRadiusMeters={mapConfig.radius}
                  enableOSM={showOsm}
                  showInterPointDistances={false}
                />
                {showOsm && osmPoisLoading ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                    <ActivityIndicator size="small" color="#2563EB" />
                    <Text style={{ marginLeft: 8, fontSize: 12, color: '#6B7280' }}>正在载入 OpenStreetMap 数据…</Text>
                  </View>
                ) : nearbySpots.length === 0 ? (
                  <Text style={{ marginTop: 8, fontSize: 12, color: '#6B7280' }}>暂未找到附近的兴趣点，稍后再试试～</Text>
                ) : null}
              </View>
            ) : null}

            {/* Tabs */}
            <View style={styles.tabsSection}>
              <TouchableOpacity
                style={[styles.tabButton, activeTab === 'posts' && styles.tabButtonActive]}
                onPress={() => setActiveTab('posts')}
              >
                <MdFlight size={20} color={activeTab === 'posts' ? '#ff6b35' : '#999'} style={{ marginBottom: 4 }} />
                <Text style={[styles.tabButtonText, activeTab === 'posts' && styles.tabButtonTextActive]}>
                  游记
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.tabButton, activeTab === 'cards' && styles.tabButtonActive]}
                onPress={() => setActiveTab('cards')}
              >
                <MdStyle size={20} color={activeTab === 'cards' ? '#ff6b35' : '#999'} style={{ marginBottom: 4 }} />
                <Text style={[styles.tabButtonText, activeTab === 'cards' && styles.tabButtonTextActive]}>
                  卡片
                </Text>
              </TouchableOpacity>
            </View>

            {/* Content */}
            <View style={styles.contentSection}>
              {activeTab === 'posts' && (
                <View>
                  {postsLoading ? (
                    <ActivityIndicator size="small" color="#ff6b35" style={{ marginTop: 20 }} />
                  ) : displayPosts.length > 0 ? (
                    displayPosts.map(post => (
                      <TouchableOpacity
                        key={post.id}
                        style={styles.postCard}
                        onPress={() => navigate(`/travel-post/${post.id}`)}
                        activeOpacity={0.7}
                      >
                        {post.cover_image && (
                          <Image source={{ uri: post.cover_image }} style={styles.coverImage} />
                        )}
                        <View style={styles.postContent}>
                          <Text style={styles.postTitle} numberOfLines={2}>{post.title}</Text>
                          {post.summary && <Text style={styles.postSummary} numberOfLines={2}>{post.summary}</Text>}
                          <View style={styles.postMeta}>
                            <View style={styles.statsRow}>
                              <View style={styles.statItem}><MdVisibility size={14} color="#999" /><Text style={styles.statText}>{post.view_count}</Text></View>
                              <Text style={styles.statDivider}>·</Text>
                              <View style={styles.statItem}><MdFavorite size={14} color="#999" /><Text style={styles.statText}>{post.like_count}</Text></View>
                              <Text style={styles.statDivider}>·</Text>
                              <View style={styles.statItem}><MdInsertComment size={14} color="#999" /><Text style={styles.statText}>{post.comment_count}</Text></View>
                            </View>
                          </View>
                        </View>
                      </TouchableOpacity>
                    ))
                  ) : (
                    <View style={{ padding: 20, alignItems: 'center' }}><Text style={{ color: '#999' }}>暂无游记</Text></View>
                  )}
                </View>
              )}

              {activeTab === 'cards' && (
                <View>
                  {/* Search & Filter */}
                  <View style={styles.searchContainer}>
                    <View style={styles.searchRow}>
                      <View style={styles.searchBox}>
                        <MdSearch size={20} color="#6B7280" />
                        <TextInput
                          style={styles.searchInput}
                          placeholder="搜索卡片..."
                          value={cardSearchQuery}
                          onChangeText={setCardSearchQuery}
                        />
                      </View>
                      <TouchableOpacity 
                        style={[styles.filterButton, showFilter && styles.filterButtonActive]}
                        onPress={() => setShowFilter(!showFilter)}
                      >
                        <MdTune size={20} color={showFilter ? '#0B61D6' : '#6B7280'} />
                      </TouchableOpacity>
                    </View>
                    
                    {showFilter && (
                      <View style={styles.cityFilterContainer}>
                        <View style={styles.cityFilterHeader}>
                          <Text style={{ fontSize: 12, fontWeight: '600', color: '#374151' }}>排序方式</Text>
                          <TouchableOpacity onPress={() => setShowFilter(false)}>
                            <MdClose size={18} color="#6B7280" />
                          </TouchableOpacity>
                        </View>
                        <View style={styles.cityChipsWrap}>
                          <TouchableOpacity
                            style={[styles.cityChip, sortOrder === 'newest' && styles.cityChipSelected]}
                            onPress={() => setSortOrder('newest')}
                          >
                            <Text style={[styles.cityChipText, sortOrder === 'newest' && styles.cityChipTextSelected]}>最新发布</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[styles.cityChip, sortOrder === 'popular' && styles.cityChipSelected]}
                            onPress={() => setSortOrder('popular')}
                          >
                            <Text style={[styles.cityChipText, sortOrder === 'popular' && styles.cityChipTextSelected]}>最多浏览</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}

                    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtersContainer}>
                      <View style={styles.filterGroup}>
                        {cardTypes.map(type => (
                          <TouchableOpacity
                            key={type.key}
                            style={[styles.filterChip, selectedCardTypes.includes(type.key) && styles.filterChipActive]}
                            onPress={() => {
                              setSelectedCardTypes(prev => prev.includes(type.key) ? prev.filter(t => t !== type.key) : [...prev, type.key]);
                            }}
                          >
                            <PlatformIcon name={type.icon} size={16} color={selectedCardTypes.includes(type.key) ? '#0B61D6' : '#6B7280'} />
                            <Text style={[styles.filterChipText, selectedCardTypes.includes(type.key) && styles.filterChipTextActive]}>
                              {type.label}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </ScrollView>
                  </View>

                  {cardsLoading ? (
                    <ActivityIndicator size="small" color="#ff6b35" style={{ marginTop: 20 }} />
                  ) : filteredCards.length > 0 ? (
                    <View style={styles.gridContainer}>
                      {filteredCards.map(c => (
                        <View key={c.id} style={styles.gridItem}>
                          <CompactCard
                            cardId={c.id}
                            title={c.title}
                            subtitle={formatAddress(c)}
                            imageUrl={c.cover_image}
                          />
                        </View>
                      ))}
                    </View>
                  ) : (
                    <View style={{ padding: 20, alignItems: 'center' }}><Text style={{ color: '#999' }}>暂无卡片</Text></View>
                  )}
                </View>
              )}
            </View>
          </>
        )}
        </View>
      </View>

    </View>

    {/* Full-screen POI editor for creating cards from map - use Portal to ensure it's rendered last (on top) */}
    {showPoiModal && typeof document !== 'undefined' && (() => {
      // Create or reuse a container div that's always the last child of body
      let container = document.getElementById('poi-modal-portal');
      if (!container) {
        container = document.createElement('div');
        container.id = 'poi-modal-portal';
        document.body.appendChild(container);
      } else {
        // Ensure it's always last
        document.body.appendChild(container);
      }
      
      return createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2147483647,
          pointerEvents: 'auto',
        }}>
          <FullscreenPoiModal
            visible={!!showPoiModal}
            initialEditMode={true}
            editableInline={false}
            card={undefined}
            title={poiPayload?.title}
            imageUrl={poiPayload?.imageUrl}
            address={poiPayload?.address}
            description={poiPayload?.description}
            metaData={poiPayload?.metaPairs}
            tags={poiPayload?.tags}
            rawData={poiPayload}
            supabaseClient={supabase}
            currentUserId={currentUserId}
            canEdit={true}
            isOwner={true}
            onClose={() => {
              setShowPoiModal(false);
              setPoiPayload(null);
            }}
            onLoadedCanonical={async (card: any) => {
              console.log('[CityPage] Card saved to cards_cards table:', card);
              try { 
                showSuccess && showSuccess('卡片已保存');
              } catch (e) {
                console.error('Toast error:', e);
              }
              // Refresh the card list for this type by fetching the full card data
              if (card && card.card_type && card.id) {
                try {
                  const { data: fullCard } = await supabase
                    .from('cards_cards')
                    .select('id,title,description,cover_image,address,card_type,base_payload,latitude,longitude,price_range')
                    .eq('id', card.id)
                    .single();
                  
                  if (fullCard) {
                    setCardsByType((prev) => {
                      const existing = prev[card.card_type] || [];
                      // Add to the beginning if not already present
                      const alreadyExists = existing.some((c) => c.id === card.id);
                      return {
                        ...prev,
                        [card.card_type]: alreadyExists ? existing : [fullCard, ...existing],
                      };
                    });
                  }
                } catch (err) {
                  console.error('[CityPage] Failed to fetch full card data:', err);
                }
              }
              setShowPoiModal(false);
              setPoiPayload(null);
              // Navigate to the card detail page
              if (card && card.id) {
                navigate(`/card/${card.id}`);
              }
            }}
          />
        </div>,
        container
      );
    })()}
    </>
  );
};

// Small helper to render a titled list of cards
const CardListSection: React.FC<{ title: string; children?: React.ReactNode; layout?: 'list' | 'grid'; count?: number }> = ({ title, children, layout = 'grid', count }) => {
  const isGrid = layout === 'grid';
  return (
    <View style={{ marginBottom: 12 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text style={styles.sectionTitle}>{title}</Text>
        {typeof count === 'number' ? <Text style={{ fontSize: 12, color: '#6B7280' }}>{count} 项</Text> : null}
      </View>
      <View style={isGrid ? styles.gridContainer : undefined}>
        {React.Children.map(children, (child, idx) => (
          <View key={idx} style={isGrid ? styles.gridItem : undefined}>
            {child}
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f6f7f8' },
  inner: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
    backgroundColor: '#ffffff',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    boxShadow: '0 6px 18px rgba(16,24,40,0.06)' as any,
    overflow: 'hidden' as any,
    padding: 20,
  },
  heroOuterContainer: {
    width: '100%',
    alignItems: 'center',
  },
  heroInnerContainer: {
    maxWidth: 600,
    width: '100%',
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    overflow: 'hidden' as any,
    boxShadow: '0 6px 18px rgba(16,24,40,0.06)' as any,
  },
  heroWrap: Platform.select({
    web: {
      position: 'relative',
      width: '100%',
      height: 220,
      overflow: 'hidden',
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    },
    default: { position: 'relative', width: '100%', height: 220 },
  }),
  heroImage: { width: '100%', height: '100%', resizeMode: 'cover' },
  backButton: {
    position: 'absolute',
    left: 14,
    top: 14,
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex: 50,
  },
  heroOverlap: {
    marginTop: -72, // pull image up into header area (HEADER_MIN_HEIGHT)
  },
  heroOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.15)',
    zIndex: 5,
    pointerEvents: 'none',
  },
  heroText: {
    position: 'absolute',
    left: 20,
    bottom: 20,
    right: 100,
    zIndex: 20,
  },
  heroTitle: { color: '#fff', fontSize: 28, fontWeight: '700', marginBottom: 6 },
  heroIntro: { color: '#fff', fontSize: 14, lineHeight: 20, opacity: 0.95 },
  shareButton: {
    position: 'absolute',
    right: 14,
    top: 14,
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  shareImage: {
    width: 40,
    height: 40,
    resizeMode: 'cover',
  },
  content: { padding: 0 },
  heading: { fontSize: 20, fontWeight: '700', marginBottom: 8 },
  description: { fontSize: 14, color: '#4B5563' },
  sectionTitle: { fontSize: 16, fontWeight: '700', marginTop: 12, marginBottom: 8 },
  placeholderCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    backgroundColor: '#F9FAFB',
    marginBottom: 12,
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12 as any,
  },
  gridItem: {
    width: '48%',
    marginBottom: 12,
  },
  // Tabs
  tabsSection: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    gap: 8,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabButtonActive: {
    borderBottomColor: '#ff6b35',
  },
  tabButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#999',
  },
  tabButtonTextActive: {
    color: '#ff6b35',
  },
  contentSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  // Post Card
  postCard: {
    marginBottom: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#f0f0f0',
  },
  coverImage: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#f0f0f0',
  },
  postContent: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  postTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  postSummary: {
    fontSize: 12,
    color: '#666',
    lineHeight: 18,
    marginBottom: 8,
  },
  postMeta: {
    gap: 6,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statText: {
    fontSize: 11,
    color: '#999',
  },
  statDivider: {
    fontSize: 11,
    color: '#ccc',
    marginHorizontal: 6,
  },
  // Search & Filter
  searchContainer: { marginBottom: 12 },
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 12 },
  searchBox: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 20, paddingHorizontal: 16, paddingVertical: 8 },
  searchInput: { flex: 1, marginLeft: 12, fontSize: 14, color: '#111827', outlineWidth: 0, outlineColor: 'transparent' },
  filterButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterButtonActive: {
    backgroundColor: '#E6F0FF',
  },
  cityFilterContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  cityFilterHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  cityChipsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  cityChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
  },
  cityChipSelected: {
    backgroundColor: '#E6F0FF',
  },
  cityChipText: {
    fontSize: 12,
    color: '#4B5563',
  },
  cityChipTextSelected: {
    color: '#0B61D6',
    fontWeight: '600',
  },
  filtersContainer: { marginBottom: 12 },
  filterGroup: { flexDirection: 'row', alignItems: 'center', paddingRight: 24 },
  filterChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, marginRight: 8 },
  filterChipActive: { backgroundColor: '#E6F0FF' },
  filterChipText: { fontSize: 13, color: '#6B7280', marginLeft: 4 },
  filterChipTextActive: { color: '#0B61D6', fontWeight: '600' },
});

export default CityPage;

// Affiliate widget removed — rendering and injection logic disabled per request.

const CompactCard: React.FC<{ cardId?: string; title: string; subtitle?: string; imageUrl?: string }> = ({ cardId, title, subtitle, imageUrl }) => {
  const navigate = useNavigate();
  const handlePress = () => {
    if (!cardId) return;
    navigate(`/card/${cardId}`);
  };

  return (
    <TouchableOpacity style={compactStyles.card} activeOpacity={0.95} onPress={handlePress}>
      <Image source={{ uri: imageUrl || '/assets/images/destinations/tokyo.jpg' }} style={compactStyles.image as any} />
      <View style={compactStyles.body}>
        <Text style={compactStyles.title} numberOfLines={2}>{title}</Text>
        {subtitle ? <Text style={compactStyles.subtitle} numberOfLines={1}>{subtitle}</Text> : null}
      </View>
    </TouchableOpacity>
  );
};

const compactStyles = StyleSheet.create({
  card: { backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden', borderWidth: 1, borderColor: '#E5E7EB' },
  image: { width: '100%', aspectRatio: 16 / 10, backgroundColor: '#F3F4F6' },
  body: { padding: 10 },
  title: { fontSize: 14, fontWeight: '600', color: '#111827' },
  subtitle: { marginTop: 6, fontSize: 12, color: '#6B7280' },
});

// Empty hint after data loaded but no items available
const EmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <View style={{
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    backgroundColor: '#F9FAFB',
  }}>
    <Text style={{ color: '#6B7280', fontSize: 13 }}>{text}</Text>
  </View>
);

// helper: derive a short subtitle from card row
const formatAddress = (c: any): string => {
  try {
    if (!c) return '';
    // prefer address column if string
    if (c.address && typeof c.address === 'string' && c.address.trim()) return c.address;
    // if address is object
    if (c.address && typeof c.address === 'object') {
      if (c.address.city) return `${c.address.city}${c.address.district ? ' · ' + c.address.district : ''}`;
      if (c.address.formatted_address) return c.address.formatted_address;
    }
    // fallback to location json
    if (c.location) {
      if (typeof c.location === 'string') {
        const s = c.location.trim();
        if (s) return s;
      }
      if (typeof c.location === 'object') {
        if (c.location.city) return `${c.location.city}${c.location.district ? ' · ' + c.location.district : ''}`;
        if (c.location.formatted_address) return c.location.formatted_address;
      }
    }
    // base_payload placeName
    if (c.base_payload && typeof c.base_payload === 'object' && c.base_payload.placeName) return c.base_payload.placeName;
    return '';
  } catch (e) {
    return '';
  }
};
